import React, { useEffect, useState } from "react";
import { Topbar } from "@/components/layout/Topbar";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Save, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { RaceSelect } from "@/components/rikkus/RaceSelect";
import { fractionalToDecimal, isValidDecimalOdds, fmtOdds } from "@/lib/odds";
import { useSelectedDate } from "@/context/DateContext";

export default function Bet365Page() {
    const { date } = useSelectedDate();
    const [raceId, setRaceId] = useState(null);
    const [entries, setEntries] = useState([]);
    const [rows, setRows] = useState([]); // {program_number, fractional, decimal}
    const [saving, setSaving] = useState(false);
    const [existing, setExisting] = useState([]);

    // Load entries when race changes
    useEffect(() => {
        if (!raceId) { setEntries([]); setRows([]); setExisting([]); return; }
        api.get(`/races/${raceId}/entries`).then((r) => {
            const ents = (r.data || []).filter(e => !e.scratched);
            setEntries(ents);
            setRows(ents.map(e => ({ program_number: e.program_number, fractional: "", decimal: "" })));
        }).catch(() => setEntries([]));
        api.get(`/races/${raceId}/snapshots?source=BET365`).then((r) => setExisting(r.data || [])).catch(() => setExisting([]));
    }, [raceId]);

    const updateRow = (i, field, value) => {
        setRows((prev) => {
            const copy = [...prev];
            copy[i] = { ...copy[i], [field]: value };
            if (field === "fractional") {
                const dec = fractionalToDecimal(value);
                copy[i].decimal = dec != null ? String(Number(dec).toFixed(2)) : "";
            }
            return copy;
        });
    };

    const addRow = () => {
        setRows((p) => [...p, { program_number: "", fractional: "", decimal: "" }]);
    };
    const removeRow = (i) => setRows((p) => p.filter((_, idx) => idx !== i));

    const submit = async () => {
        if (!raceId) return toast.error("Selecciona carrera");
        const cleaned = rows.filter(r => r.program_number && (r.decimal || r.fractional));
        if (cleaned.length === 0) return toast.error("Sin filas válidas");
        // Validación cliente
        for (const r of cleaned) {
            const dec = r.decimal ? parseFloat(r.decimal) : fractionalToDecimal(r.fractional);
            if (!isValidDecimalOdds(dec)) {
                return toast.error(`Odds fuera de rango [1.01, 1000] en #${r.program_number}: ${r.decimal || r.fractional}`);
            }
        }
        const body = {
            race_id: raceId,
            rows: cleaned.map(r => ({
                program_number: r.program_number,
                win_odds_decimal: r.decimal ? parseFloat(r.decimal) : null,
                win_odds_fractional: r.fractional || null,
            })),
        };
        setSaving(true);
        try {
            const { data } = await api.post("/bet365-snapshots", body);
            const skipMsg = Object.keys(data.skipped_reasons || {}).length > 0
                ? ` (saltadas: ${JSON.stringify(data.skipped_reasons)})` : "";
            toast.success(`Snapshot BET365 guardado: ${data.inserted} filas${skipMsg}`);
            api.get(`/races/${raceId}/snapshots?source=BET365`).then((r) => setExisting(r.data || [])).catch(() => {});
            // Limpiar valores de la fila (mantener program numbers)
            setRows((p) => p.map(r => ({ ...r, fractional: "", decimal: "" })));
        } catch (err) {
            toast.error(err?.response?.data?.detail || err.message || "Error");
        } finally { setSaving(false); }
    };

    return (
        <>
            <Topbar title="Bet365 — precios manuales" subtitle="Puente hasta el ingestor automático (fixed odds • sin pools)" />
            <div className="p-3 md:p-4 space-y-4 flex-1 overflow-auto">
                <Card>
                    <CardHeader className="pb-3">
                        <CardTitle className="text-sm">Nueva captura</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                            <div className="md:col-span-2">
                                <Label className="text-xs mb-1 block">Carrera (fecha global: {date})</Label>
                                <RaceSelect value={raceId} onChange={setRaceId} testid="bet365-race-select" />
                            </div>
                            <div>
                                <Label className="text-xs mb-1 block">Captured at (default = ahora UTC)</Label>
                                <Input value="ahora" disabled className="h-9 font-mono text-muted-foreground" />
                            </div>
                        </div>

                        <div className="rounded-md border border-border overflow-hidden">
                            <Table data-testid="bet365-odds-table">
                                <TableHeader>
                                    <TableRow className="bg-muted/40 hover:bg-muted/40">
                                        <TableHead className="h-8 text-[11px]">#</TableHead>
                                        <TableHead className="h-8 text-[11px]">Fraccional</TableHead>
                                        <TableHead className="h-8 text-[11px]">Decimal</TableHead>
                                        <TableHead className="h-8 text-[11px]">Impl</TableHead>
                                        <TableHead className="h-8 text-[11px] w-10"></TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {rows.length === 0 && (
                                        <TableRow><TableCell colSpan={5} className="text-center text-xs text-muted-foreground py-6">Selecciona una carrera para cargar caballos.</TableCell></TableRow>
                                    )}
                                    {rows.map((r, i) => {
                                        const dec = r.decimal ? parseFloat(r.decimal) : null;
                                        const inRange = dec == null || isValidDecimalOdds(dec);
                                        const impl = dec ? (1 / dec) : null;
                                        return (
                                            <TableRow key={i} className="h-9" data-testid={`bet365-odds-row-${r.program_number || i}`}>
                                                <TableCell className="py-1">
                                                    <Input value={r.program_number} onChange={(e) => updateRow(i, "program_number", e.target.value)} className="h-7 font-mono w-16" />
                                                </TableCell>
                                                <TableCell className="py-1">
                                                    <Input
                                                        data-testid={`bet365-fractional-odds-input-${r.program_number || i}`}
                                                        value={r.fractional}
                                                        onChange={(e) => updateRow(i, "fractional", e.target.value)}
                                                        placeholder="5/2"
                                                        className="h-7 font-mono w-24"
                                                    />
                                                </TableCell>
                                                <TableCell className="py-1">
                                                    <Input
                                                        data-testid={`bet365-decimal-odds-input-${r.program_number || i}`}
                                                        value={r.decimal}
                                                        onChange={(e) => updateRow(i, "decimal", e.target.value)}
                                                        placeholder="3.50"
                                                        className={`h-7 font-mono w-24 ${!inRange ? "border-fail text-fail" : ""}`}
                                                    />
                                                </TableCell>
                                                <TableCell className="py-1 font-mono text-xs text-muted-foreground">
                                                    {impl != null ? impl.toFixed(4) : "—"}
                                                </TableCell>
                                                <TableCell className="py-1 text-right">
                                                    <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => removeRow(i)} data-testid={`bet365-remove-row-${i}`}>
                                                        <Trash2 className="h-3.5 w-3.5 text-muted-foreground" />
                                                    </Button>
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </div>

                        <div className="flex items-center gap-2">
                            <Button variant="outline" size="sm" className="h-8 text-xs" onClick={addRow} data-testid="bet365-add-row">
                                <Plus className="h-3.5 w-3.5 mr-1" /> Añadir fila
                            </Button>
                            <Button data-testid="bet365-save-button" onClick={submit} disabled={saving || !raceId} className="h-8 text-xs">
                                <Save className="h-3.5 w-3.5 mr-1" />
                                {saving ? "Guardando…" : "Guardar snapshot BET365"}
                            </Button>
                        </div>
                    </CardContent>
                </Card>

                {existing.length > 0 && (
                    <Card>
                        <CardHeader className="pb-3">
                            <CardTitle className="text-sm">Snapshots BET365 previos (esta carrera)</CardTitle>
                        </CardHeader>
                        <CardContent>
                            <div className="rounded-md border border-border overflow-hidden">
                                <Table>
                                    <TableHeader>
                                        <TableRow className="bg-muted/40 hover:bg-muted/40">
                                            <TableHead className="h-8 text-[11px]">Snapshot</TableHead>
                                            <TableHead className="h-8 text-[11px]">#</TableHead>
                                            <TableHead className="h-8 text-[11px]">Captured (UTC)</TableHead>
                                            <TableHead className="h-8 text-[11px] text-right">Win odds</TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {existing.map((s) => (
                                            <TableRow key={s.snapshot_id} className="h-8">
                                                <TableCell className="font-mono text-xs">{s.snapshot_id}</TableCell>
                                                <TableCell className="font-mono text-xs">{s.program_number}</TableCell>
                                                <TableCell className="font-mono text-xs text-muted-foreground">{s.captured_at}</TableCell>
                                                <TableCell className="font-mono text-xs text-right">{fmtOdds(s.win_odds)}</TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </div>
                        </CardContent>
                    </Card>
                )}
            </div>
        </>
    );
}
